/**
 * Transfer Configuration for Products
 * Uses total_base_units for lot selection.
 * Inserts product_inventory_transactions with type 'transfer'.
 * Re-packs source lot after deduction; adds tier quantities to destination.
 * Supports variants and full pallet/case/unit breakdown.
 */

import { supabase } from '../../../../../supabaseClient';

export const transferProductConfig = {
  type:              'products',
  displayName:       'Products',
  singularName:      'Product',
  itemIdField:       'product_id',
  itemNameField:     'name',

  inventoryTable:    'product_inventory',
  itemsTable:        'product_inventory_items',
  transactionsTable: 'product_inventory_transactions',

  /**
   * Get available lot items for a product at a location.
   * Returns current_quantity alias (= total_base_units) and variant_name.
   */
  getInventoryItems: async (companyId, itemId, locationId) => {
    const { data, error } = await supabase
      .from('product_inventory_items')
      .select(`
        *,
        products (
          id, name, sku
        ),
        product_variants (id, name)
      `)
      .eq('company_id', companyId)
      .eq('product_id', itemId)
      .eq('location_id', locationId)
      .eq('is_active', true)
      .gt('total_base_units', 0)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return (data || []).map(item => ({
      ...item,
      current_quantity: item.total_base_units,
      variant_name: item.product_variants?.name || null
    }));
  },

  /**
   * Execute transfer transactions for all selected lots.
   * Source lot is re-packed after deduction (handles tier breakdown).
   * Destination lot receives the transferred pallet/case/unit quantities.
   */
  executeTransfer: async (companyId, transferData) => {
    const { fromLocationId, toLocationId, transfers, notes } = transferData;

    for (const transfer of transfers) {
      const { itemId, variantId, lotId, pallets = 0, cases = 0, units = 0, lot_number, batch_number } = transfer;

      // Fetch source lot for config + current quantities
      const { data: lotData } = await supabase
        .from('product_inventory_items')
        .select('*')
        .eq('id', lotId)
        .single();

      const resolvedVariantId = variantId || lotData?.variant_id || null;
      const unitsPerCase      = lotData?.units_per_case   || 1;
      const casesPerPallet    = lotData?.cases_per_pallet || 1;

      // Total base units being transferred (capped at what the lot has)
      const requestedBase  = (pallets * casesPerPallet * unitsPerCase) + (cases * unitsPerCase) + units;
      const totalBaseUnits = Math.min(requestedBase, lotData?.total_base_units || 0);

      // Insert transfer transaction
      const { error: txError } = await supabase
        .from('product_inventory_transactions')
        .insert([{
          company_id:       companyId,
          product_id:       itemId,
          variant_id:       resolvedVariantId,
          transaction_type: 'transfer',
          quantity_change:  totalBaseUnits,
          from_location_id: fromLocationId,
          to_location_id:   toLocationId,
          input_pallets:    pallets,
          input_cases:      cases,
          input_units:      units,
          lot_number:       lot_number,
          batch_number:     batch_number,
          units_per_case:   unitsPerCase,
          cases_per_pallet: casesPerPallet,
          created_at:       new Date().toISOString(),
          notes:            notes || null
        }]);

      if (txError) throw txError;

      // Re-pack SOURCE lot remaining stock (same logic as usage)
      let srcLeftover = (lotData?.total_base_units || 0) - totalBaseUnits;
      let newSrcPallets = 0, newSrcCases = 0, newSrcUnits = 0;

      if (lotData?.enable_pallets && casesPerPallet > 0) {
        newSrcPallets = Math.floor(srcLeftover / (casesPerPallet * unitsPerCase));
        srcLeftover  -= newSrcPallets * casesPerPallet * unitsPerCase;
      }
      if (lotData?.enable_cases && unitsPerCase > 0) {
        newSrcCases  = Math.floor(srcLeftover / unitsPerCase);
        srcLeftover -= newSrcCases * unitsPerCase;
      }
      newSrcUnits = srcLeftover;

      // If source lot is fully emptied, hard-delete the record — no ghost rows
      if (newSrcPallets === 0 && newSrcCases === 0 && newSrcUnits === 0) {
        await supabase.from('product_inventory_items').delete().eq('id', lotId);
      } else {
        await supabase
          .from('product_inventory_items')
          .update({
            pallet_quantity:  newSrcPallets,
            case_quantity:    newSrcCases,
            unit_quantity:    newSrcUnits,
            is_active:        true,
            last_updated_at:  new Date().toISOString()
          })
          .eq('id', lotId);
      }

      // Compute DESTINATION tier amounts by re-packing totalBaseUnits
      let destLeftover = totalBaseUnits;
      let destPallets = 0, destCases = 0, destUnits = 0;

      if (lotData?.enable_pallets && casesPerPallet > 0) {
        destPallets  = Math.floor(destLeftover / (casesPerPallet * unitsPerCase));
        destLeftover -= destPallets * casesPerPallet * unitsPerCase;
      }
      if (lotData?.enable_cases && unitsPerCase > 0) {
        destCases    = Math.floor(destLeftover / unitsPerCase);
        destLeftover -= destCases * unitsPerCase;
      }
      destUnits = destLeftover;

      // Find existing ACTIVE destination lot (match by product, variant, lot/batch).
      // Only match active lots — dead/soft-deleted records must NOT be picked up as merge targets.
      let destQuery = supabase
        .from('product_inventory_items')
        .select('id, pallet_quantity, case_quantity, unit_quantity, invoice_file_path, po_file_path, coa_file_path, sds_file_path, production_doc_file_path')
        .eq('company_id', companyId)
        .eq('product_id', itemId)
        .eq('location_id', toLocationId)
        .eq('lot_number',   lot_number   || '')
        .eq('batch_number', batch_number || '')
        .eq('is_active', true);

      destQuery = resolvedVariantId
        ? destQuery.eq('variant_id', resolvedVariantId)
        : destQuery.is('variant_id', null);

      const { data: existingDest } = await destQuery.limit(1).maybeSingle();

      if (existingDest) {
        // Merge tier quantities into existing destination lot.
        // Always sync unit config from source so stale configs can't persist.
        // Fill in any documents the destination lot is missing from the source.
        const newDestPallets = (existingDest.pallet_quantity || 0) + destPallets;
        const newDestCases   = (existingDest.case_quantity   || 0) + destCases;
        const newDestUnits   = (existingDest.unit_quantity   || 0) + destUnits;

        const docMerge = {};
        if (!existingDest.invoice_file_path        && lotData?.invoice_file_path)         { docMerge.invoice_file_path         = lotData.invoice_file_path;         docMerge.invoice_file_name         = lotData.invoice_file_name; }
        if (!existingDest.po_file_path             && lotData?.po_file_path)              { docMerge.po_file_path              = lotData.po_file_path;              docMerge.po_file_name              = lotData.po_file_name; }
        if (!existingDest.coa_file_path            && lotData?.coa_file_path)             { docMerge.coa_file_path             = lotData.coa_file_path;             docMerge.coa_file_name             = lotData.coa_file_name; }
        if (!existingDest.sds_file_path            && lotData?.sds_file_path)             { docMerge.sds_file_path             = lotData.sds_file_path;             docMerge.sds_file_name             = lotData.sds_file_name; }
        if (!existingDest.production_doc_file_path && lotData?.production_doc_file_path)  { docMerge.production_doc_file_path  = lotData.production_doc_file_path;  docMerge.production_doc_file_name  = lotData.production_doc_file_name; }

        await supabase
          .from('product_inventory_items')
          .update({
            pallet_quantity:   newDestPallets,
            case_quantity:     newDestCases,
            unit_quantity:     newDestUnits,
            units_per_case:    unitsPerCase,
            cases_per_pallet:  casesPerPallet,
            enable_cases:      lotData?.enable_cases  ?? true,
            enable_pallets:    lotData?.enable_pallets ?? false,
            manufactured_date: lotData?.manufactured_date || null,
            expiry_date:       lotData?.expiry_date        || null,
            shelf_life_months: lotData?.shelf_life_months  || null,
            is_active:         true,
            last_updated_at:   new Date().toISOString(),
            ...docMerge
          })
          .eq('id', existingDest.id);
      } else {
        // Create new lot at destination — carry over all metadata from source lot
        await supabase
          .from('product_inventory_items')
          .insert({
            company_id:               companyId,
            product_id:               itemId,
            variant_id:               resolvedVariantId,
            location_id:              toLocationId,
            pallet_quantity:          destPallets,
            case_quantity:            destCases,
            unit_quantity:            destUnits,
            original_pallet_quantity: destPallets,
            original_case_quantity:   destCases,
            original_unit_quantity:   destUnits,
            units_per_case:           unitsPerCase,
            cases_per_pallet:         casesPerPallet,
            enable_cases:             lotData?.enable_cases      ?? true,
            enable_pallets:           lotData?.enable_pallets     ?? false,
            lot_number:               lot_number                  || null,
            batch_number:             batch_number                || null,
            manufactured_date:        lotData?.manufactured_date  || null,
            expiry_date:              lotData?.expiry_date         || null,
            shelf_life_months:        lotData?.shelf_life_months   || null,
            invoice_file_path:        lotData?.invoice_file_path   || null,
            invoice_file_name:        lotData?.invoice_file_name   || null,
            po_file_path:             lotData?.po_file_path         || null,
            po_file_name:             lotData?.po_file_name         || null,
            coa_file_path:            lotData?.coa_file_path        || null,
            coa_file_name:            lotData?.coa_file_name        || null,
            sds_file_path:            lotData?.sds_file_path        || null,
            sds_file_name:            lotData?.sds_file_name        || null,
            production_doc_file_path: lotData?.production_doc_file_path || null,
            production_doc_file_name: lotData?.production_doc_file_name || null,
            is_active:                true,
            last_updated_at:          new Date().toISOString()
          });
      }

      // Recalculate aggregates at both locations
      await supabase.rpc('recalculate_product_inventory_aggregate', {
        p_company_id:  companyId,
        p_product_id:  itemId,
        p_variant_id:  resolvedVariantId,
        p_location_id: fromLocationId
      });
      await supabase.rpc('recalculate_product_inventory_aggregate', {
        p_company_id:  companyId,
        p_product_id:  itemId,
        p_variant_id:  resolvedVariantId,
        p_location_id: toLocationId
      });
    }
  }
};
